import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cx from 'classnames'
import { motion } from 'framer-motion';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import { Button } from 'react-bootstrap';
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip';
import { ParallaxProvider } from 'react-scroll-parallax';

import { BackgroundViewComponent } from '../background';
import { injectAktivFallback } from '../widget.helper';
import { getColorsConfig } from '../../../components/AssetViewer/helper/cssHelper';
import { useDispatch, useSelector } from 'react-redux';
import { SLIDE } from '../../../constants/widgetTypes';
import { updateHyperLinkSlide } from '../../../actions/spatial';
import { WidgetsContext } from '../../../index';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import isEqual from 'lodash/isEqual';
import { dynamicImport } from '../../../services/dynamicImport';
import { FlipbookFlipContainer } from './flipBookFlipContainer';
import styles from './flipbook.module.scss';

const KeyManager = dynamicImport(() => import('./KeyManager'));

const DESKTOP_ASPECT_RATIO = 16 / 9;
const MOBILE_ASPECT_RATIO = 9 / 16;
const DESKTOP_WIDTH_PADDING = 30;
const MOBILE_WIDTH_PADDING = 20;
const DESKTOP_HEIGHT_PADDING = 30;
const MOBILE_HEIGHT_PADDING = 60;
const HIGHLIGHT_WIDTH = 225;
const PAGES_TO_LOAD = 10;
const PAGES_TO_RENDER = 5;
const SUB_SLIDE_TRANSITION_MS = 450; // must match .subSlideCard CSS transition (400ms + 50ms buffer)
const MOBILE_SUBSLIDE_STACK_MS = 400; // vertical stack translate — keep in sync with .subSlideVerticalTrack

// ─── hooks ────────────────────────────────────────────────────────────────────

export const useDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(min-width: 992px)');
      setIsDesktop(mediaQuery.matches);
      const handler = () => setIsDesktop(mediaQuery.matches);
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, []);

  return mounted ? isDesktop : true;
};

export const useMobilePortrait = () => {
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsMobilePortrait(window.innerHeight > window.innerWidth);
      }
    };
    handleResize();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  if (!mounted) return false;
  return isMobilePortrait;
};

// ─── FlipbookContent ──────────────────────────────────────────────────────────

export const FlipbookContent = React.memo(({
  breakpoint,
  initialPage,
  pages,
  appearance,
  onChange,
  children,
  onFetchPage,
  imageCDN,
  updatePagesToRender
}) => {
  const dispatch = useDispatch();
  const containerRef        = useRef(null);
  const flipbookPageRef     = useRef(null);
  const flipbookPageInnerRef= useRef(null);
  const isAnimatingRef      = useRef(false);   // blocks concurrent flips
  const pendingPageNavRef   = useRef(null);    // deferred nav after sub-slide close
  const navigateToPageRef   = useRef(() => Promise.resolve());
  // ─ horizontal swipe tracking (page-to-page, lives HERE not in FlipbookPage) ─
  const touchStartXRef      = useRef(0);
  const touchStartYRef      = useRef(0);       // used to ignore vertical swipes
  // ─ content snapshots — frozen at flip-start so re-renders don't corrupt slots ─
  const snapshotCurrentRef  = useRef(null);
  const snapshotUpcomingRef = useRef(null);
  // pending onChange payload — fired AFTER the animation completes
  const pendingOnChangeRef  = useRef(null);
  // registered by the active FlipbookPage so Menu arrows can delegate slide logic
  const slideNavigatorRef   = useRef({ next: null, prev: null });

  const singlePage    = pages?.length === 1;
  const mobilePotrait = useMobilePortrait();
  const viewportSize  = useViewportSize();
  const desktop       = useDesktop();

  const aspectRatio   = !mobilePotrait ? DESKTOP_ASPECT_RATIO : MOBILE_ASPECT_RATIO;
  const widthPadding  = desktop ? DESKTOP_WIDTH_PADDING  : MOBILE_WIDTH_PADDING;
  const heightPadding = desktop ? DESKTOP_HEIGHT_PADDING : MOBILE_HEIGHT_PADDING;

  const [mounted, setMounted] = useState(false);

  const [state, setState] = useState({
    initialized:      false,
    dimensions:       { width: 0, height: 0 },
    viewportHeight:   '100vh',
    scale:            1,
    menuPosition:     { x: 0, y: 0 },
    showHighlight:    false,
    fullscreen:       false,
    subSlideOpen:     false,
    activePageHasSubSlides: false,
    backgroundPosition: 0,
    mobilePotrait,
    activePage: {
      id:         initialPage,
      fullscreen: singlePage,
      slideIndex: 0,
    },
    // ── flip state ──
    pendingPageId:     null,
    flipDirection:     null,   // 'next' | 'prev' | null
    pendingSlideIndex: 0,
    gatingActive:      false,
  });

  // ── lifecycle ────────────────────────────────────────────────────────────────

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    setState(s => ({ ...s, mobilePotrait }));
  }, [mobilePotrait]);

  useEffect(() => {
    if (!mounted) return;
    const timer = setTimeout(() => {
      setState(s => ({ ...s, initialized: true }));
    }, 1000);
    onChange('index', { id: state.activePage.id, slideIndex: 0 });
    return () => clearTimeout(timer);
  }, [mounted]); // eslint-disable-line

  useEffect(() => {
    if (!initialPage) return;
    setState(s => ({ ...s, activePage: { ...s.activePage, id: initialPage } }));
  }, [initialPage]);

  useEffect(() => {
    if (!mounted) return;
    handleResize(state.showHighlight);
  }, [viewportSize, state.showHighlight, mounted]); // eslint-disable-line

  // menu position maths
  useEffect(() => {
    if (!mounted) return;
    const menuElement = document.getElementById('cs-flipbook-menu');
    if (state.dimensions.width === 0 || !flipbookPageInnerRef.current || !menuElement) return;

    const menuRect              = menuElement.getBoundingClientRect();
    const flipbookPageRect      = flipbookPageRef.current.getBoundingClientRect();
    const flipbookPageInnerRect = flipbookPageInnerRef.current.getBoundingClientRect();

    let menuX;
    let menuY;
    if (desktop) {
      const widthDiff            = flipbookPageRect.width - flipbookPageInnerRect.width;
      const flipbookPageCenterX  = state.showHighlight
        ? flipbookPageInnerRect.width / 2 + 50
        : flipbookPageInnerRect.width / 2 + widthDiff / 2;
      menuX = flipbookPageCenterX - menuRect.width / 2;
      menuY = state.fullscreen
        ? flipbookPageInnerRect.bottom - (menuRect.height + 20)
        : flipbookPageInnerRect.bottom - menuRect.height / 2;
    } else {
      // Phone + iPad portrait (<992px): center toolbar on the card container x-axis.
      menuX = flipbookPageRect.left + (flipbookPageRect.width - menuRect.width) / 2;
      menuY = flipbookPageInnerRect.bottom - menuRect.height - 20;
    }

    setState(s => ({
      ...s,
      menuPosition: { x: menuX, y: menuY },
      highlight: { ...s.highlight, x: flipbookPageInnerRect.width + 80, y: flipbookPageInnerRect.top },
    }));
  }, [state.dimensions, state.initialized, mounted, desktop, state.fullscreen, state.showHighlight]); // eslint-disable-line


  useEffect(() => {
    if (state.subSlideOpen || !pendingPageNavRef.current) return;
    const nav = pendingPageNavRef.current;
    pendingPageNavRef.current = null;

    // Mobile: unchanged — single delay then page flip.
    // Desktop/tablet: wait for sub-slide card to slide out (cover stays static underneath), then flip.
    if (nav.desktopCrossPageChoreography) {
      const delay = SUB_SLIDE_TRANSITION_MS;
      const timer = setTimeout(() => {
        isAnimatingRef.current = false;
        navigateToPageRef.current(nav.id, nav.fullscreen, nav.slideIndex, nav.isManual);
      }, delay);
      return () => clearTimeout(timer);
    }

    const delay = nav.closeDelayMs ?? SUB_SLIDE_TRANSITION_MS;
    const timer = setTimeout(() => {
      isAnimatingRef.current = false;
      navigateToPageRef.current(nav.id, nav.fullscreen, nav.slideIndex, nav.isManual);
    }, delay);
    return () => clearTimeout(timer);
  }, [state.subSlideOpen]); // eslint-disable-line

  // side-effects that depend on the active page
  useEffect(() => {
    if (!mounted) return;
    calculateBackgroundPosition();
    handleLazyLoadPages();
    handleUpdatePagesToRender();
  }, [state.activePage.id, state.initialized, mounted]); // eslint-disable-line

  // ── helpers ──────────────────────────────────────────────────────────────────

  const handleLazyLoadPages = () => {
    const activeIndex = pages.findIndex(obj => obj.id === state.activePage.id);
    if (activeIndex < 0) return;
    let closestIndices = getClosestIndices(pages, activeIndex, PAGES_TO_LOAD);
    closestIndices = [...closestIndices, activeIndex];
    closestIndices.map(i => pages[i]).forEach(page => {
      if (page.hidden) return;
      if (page.loading || page.loaded) return;
      if (onFetchPage) onFetchPage(page.id);
    });
  };

  const handleUpdatePagesToRender = () => {
    const activeIndex    = pages.findIndex(obj => obj.id === state.activePage.id);
    const closestIndices = getClosestIndices(pages, activeIndex, PAGES_TO_RENDER);
    const closestIds     = closestIndices.map(i => pages[i]).filter(p => !p.hidden).map(p => p.id);
    const unique         = uniq([...closestIds, state.activePage.id]);
    if (updatePagesToRender) updatePagesToRender(unique, state.activePage.id);
  };

  const getClosestIndices = (pages, index, limit) => {
    const result = [];
    const len    = pages.length;
    if (len <= limit) return pages.map((_, i) => i);
    for (let i = index - 1; i >= 0 && result.length < limit / 2; i--)  result.unshift(i);
    for (let i = index + 1; i < len  && result.length < limit;     i++) result.push(i);
    for (let i = index - 2; i >= 0   && result.length < limit;     i--) result.unshift(i);
    return result;
  };

  const handleResize = useCallback((showHighlight) => {
    if (!mounted || !viewportSize || viewportSize.width === 0) return;
    const { width, height, viewportHeight } = viewportSize;

    const calculateAvailableWidth = () => {
      return showHighlight ? width - ((HIGHLIGHT_WIDTH * state.scale) + 120) : width - widthPadding;
    };

    const availableWidth  = calculateAvailableWidth();
    const availableHeight = showHighlight ? height : height - heightPadding;
    let newHeightWidth = availableWidth / aspectRatio;
    let newWidthHeight = availableWidth;
    if (newHeightWidth > availableHeight) {
      newWidthHeight = availableHeight * aspectRatio;
      newHeightWidth = availableHeight;
    }

    const scale = newHeightWidth === 1024 ? newHeightWidth / 500 : newHeightWidth / 675;

    setState(s => ({
      ...s,
      scale,
      showHighlight,
      viewportHeight,
      dimensions: { width: newWidthHeight, height: newHeightWidth },
    }));
  }, [viewportSize, state.showHighlight, mounted]); // eslint-disable-line

  const calculateBackgroundPosition = () => {
    if (!mounted) return;
    const backgroundImage = document.getElementById('cs-flipbook-bg');
    if (!backgroundImage) return;
    const backgroundImageRect = backgroundImage.getBoundingClientRect();
    const containerRect       = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    const activeIndex = pages.findIndex(obj => obj.id === state.activePage.id);
    const position = (backgroundImageRect.width - containerRect.width) / pages?.length * activeIndex;
    setState(s => ({ ...s, backgroundPosition: position }));
  };

  const setSubSlideOpenCtx = (open) => {
    setState(s => ({ ...s, subSlideOpen: open }));
  };

  const handleToggleFullscreen = () => {
    const isFs = document.fullscreenElement || document.webkitFullscreenElement
      || document.mozFullScreenElement || document.msFullscreenElement;

    if (isFs) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    } else {
      const el = containerRef.current || document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen
        || el.mozRequestFullScreen || el.msRequestFullscreen;
      if (req) req.call(el);
    }
  };

  // ── navigateToPage (flip trigger) ────────────────────────────────────────────

  const navigateToPage = (id, fullscreen, slideIndex = 0, isManual = false, navOptions = {}) => {
    const currentId = state.activePage.id;

    if (isAnimatingRef.current) return Promise.resolve();

    // ── If sub-slides are open, close them first then navigate ──────────────
    // Store the target, close sub-slides, block navigation.
    // A useEffect watching state.subSlideOpen will pick up the pending nav
    // after the close animation finishes.
    if (state.subSlideOpen && id !== currentId) {
      const isMobileBp = breakpoint === 'mobile';
      pendingPageNavRef.current = {
        id,
        fullscreen,
        slideIndex,
        isManual,
        closeDelayMs: navOptions.closeDelayMs ?? SUB_SLIDE_TRANSITION_MS,
        desktopCrossPageChoreography: !isMobileBp,
      };
      isAnimatingRef.current = true;
      setState(s => ({ ...s, subSlideOpen: false }));
      return Promise.resolve();
    }

     if (id === currentId) {
       setState(s => ({
         ...s,
         activePage: { ...s.activePage, slideIndex: slideIndex ?? 0 },
       }));
       return Promise.resolve();
    }

    const childrenArray = React.Children.toArray(children);
    const activeIndex   = pages.findIndex(p => p.id === currentId);
    const targetIndex   = pages.findIndex(p => p.id === id);
    if (targetIndex < 0)        return Promise.resolve();

    const direction = targetIndex > activeIndex ? 'next' : 'prev';

    if (isManual) {
      dispatch(updateHyperLinkSlide({ page: '', slide: '', uid: '' }));
    }

    pendingOnChangeRef.current = { id, slideIndex };
    isAnimatingRef.current = true;

    // Pre-render the upcoming page's widgets synchronously so they are mounted
    // before the animation slots capture their snapshots. Without this, the rAF
    // call happened AFTER setState(flipDirection), causing widgets to mount
    // inside the animation slots mid-flip and producing a visible glitch.
    if (updatePagesToRender) {
      updatePagesToRender([currentId, id], currentId);
    }

    // Defer snapshot capture + animation start to the next frame so React can
    // commit the pre-render above first. The snapshots then capture fully-mounted
    // widget trees instead of empty stubs.
    requestAnimationFrame(() => {
      const freshChildren = React.Children.toArray(children);
      snapshotCurrentRef.current  = freshChildren[activeIndex]  ?? null;
      snapshotUpcomingRef.current = freshChildren[targetIndex]  ?? null;

      setState(s => ({
        ...s,
        pendingPageId:     id,
        flipDirection:     direction,
        pendingSlideIndex: slideIndex,
      }));
    });

    return Promise.resolve();
  };

  navigateToPageRef.current = navigateToPage;

  // ── handleFlipComplete — called by FlipbookFlipContainer timer ───────────────

  const handleFlipComplete = useCallback(() => {
    setState(s => {
      // Fire deferred onChange only for page-to-page flips
      if (pendingOnChangeRef.current && s.pendingPageId !== s.activePage.id) {
        onChange('index', pendingOnChangeRef.current);
        pendingOnChangeRef.current = null;
      }

      // Clear snapshots — live children take over again
      snapshotCurrentRef.current  = null;
      snapshotUpcomingRef.current = null;

      isAnimatingRef.current = false;

      return {
        ...s,
        activePage: {
          ...s.activePage,
          id:         s.pendingPageId,
          slideIndex: s.pendingSlideIndex ?? 0,
        },
        pendingPageId:     null,
        flipDirection:     null,
        pendingSlideIndex: 0,
      };
    });
  }, [onChange]);

  // ── Horizontal swipe handlers (page-to-page navigation, desktop carousel) ────
  // These live on the outer <motion.div> carousel and handle LEFT/RIGHT swipes.
  // Vertical touch events are intentionally ignored here; FlipbookPage handles
  // UP/DOWN for slide scrolling.

  const handleCarouselTouchStart = useCallback((e) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  }, []);

  const handleCarouselTouchEnd = useCallback((e) => {
    if (state.subSlideOpen) return; // sub-slide card handles its own swipe

    const deltaX = touchStartXRef.current - e.changedTouches[0].clientX;
    const deltaY = touchStartYRef.current - e.changedTouches[0].clientY;

    // Ignore if predominantly vertical (user is scrolling slides, not pages)
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;

    const threshold   = 50;
    if (Math.abs(deltaX) < threshold) return;

    const activeIndex = pages.findIndex(p => p.id === state.activePage.id);

    if (deltaX > 0 && activeIndex < pages.length - 1) {
      navigateToPage(pages[activeIndex + 1].id, state.fullscreen, 0, true);
    } else if (deltaX < 0 && activeIndex > 0) {
      navigateToPage(pages[activeIndex - 1].id, state.fullscreen, 0, true);
    }
  }, [state.activePage.id, state.fullscreen, state.subSlideOpen, pages]); // eslint-disable-line

  // ── SSR guard ────────────────────────────────────────────────────────────────

  if (!mounted) {
    return (
      <div style={{ height: '100vh' }} className={cx(styles.carousel)}>
        <div className={styles.loading}>
          <div className={styles.loader}>
            <video loop autoPlay playsInline muted>
              <source src='/images/shortLoader.mp4' type='video/mp4' />
            </video>
            <label>Loading Flipbook content...</label>
          </div>
        </div>
      </div>
    );
  }

  // ── resolve current + upcoming children ──────────────────────────────────────
  // During a flip: use the snapshots frozen at navigate-time (immune to re-renders).
  // When idle:     use live children so the active page always reflects latest data.

  const childrenArray = React.Children.toArray(children);
  const activeIndex   = pages.findIndex(p => p.id === state.activePage.id);

  const isFlipping = !!state.flipDirection;

  const currentContent = isFlipping
    ? snapshotCurrentRef.current                   // frozen snapshot
    : (childrenArray[activeIndex] ?? null);        // live child

  const upcomingContent = isFlipping
    ? snapshotUpcomingRef.current                  // frozen snapshot
    : null;                                        // nothing to show when idle

  // ── render ───────────────────────────────────────────────────────────────────

  return (
    <FlipbookContext.Provider value={{
      ...state,
      breakpoint,
      pages,
      appearance,
      navigateToPage,
      setSubSlideOpen: setSubSlideOpenCtx,
      onToggleFullscreen: handleToggleFullscreen,
      onChange,
      // ── slide-aware navigation (registered by FlipbookPage) ──────────────────
      registerSlideNavigator:  (nav) => {
        slideNavigatorRef.current = nav;
        const has = !!(nav && nav.hasSubSlides);
        setState(s => (s.activePageHasSubSlides === has ? s : { ...s, activePageHasSubSlides: has }));
      },
      navigateNextAction:      ()    => { slideNavigatorRef.current?.next?.(); },
      navigatePrevAction:      ()    => { slideNavigatorRef.current?.prev?.(); },
      toggleSubSlidesAction:   ()    => { slideNavigatorRef.current?.toggleSubSlides?.(); },
      setGatingActive:         (active) => setState(s => s.gatingActive === !!active ? s : { ...s, gatingActive: !!active }),
    }}>
      <Suspense fallback={null}>
        <KeyManager>
          <motion.div
            key={breakpoint}
            ref={containerRef}
            style={{ height: state.viewportHeight }}
            className={cx(styles.carousel)}
            {...(breakpoint === 'mobile' ? {
              onTouchStart: handleCarouselTouchStart,
              onTouchEnd:   handleCarouselTouchEnd,
            } : {})}
          >
            {!state.initialized && (
              <div className={styles.loading}>
                <div className={styles.loader}>
                  <video loop autoPlay playsInline muted>
                    <source src='/images/shortLoader.mp4' type='video/mp4' />
                  </video>
                  <label>Loading Flipbook content...</label>
                </div>
              </div>
            )}

            <Suspense fallback={null}>
              <Menu
                appearance={appearance}
                scale={state.scale}
                menuPosition={state.menuPosition}
                onToggleHighlight={handleResize.bind(this, !state.showHighlight)}
                onToggleFullscreen={handleToggleFullscreen}
                activePage={state.activePage}
                navigateToPage={navigateToPage}
                breakpoint={breakpoint}
                pages={pages}
                fullscreen={state.fullscreen}
              />
            </Suspense>

            <ParallaxProvider>
              {/* Invisible placeholder for menu position maths */}
              <div
                className={cx(styles.flipbookPage, styles.flipbookPagePlaceholder)}
                ref={flipbookPageRef}
              >
                <div
                  className={styles.flipbookPagePlaceholderInner}
                  ref={flipbookPageInnerRef}
                  style={{
                    width:           state.dimensions.width,
                    height:          state.dimensions.height,
                    transformOrigin: 'center top',
                  }}
                />
              </div>

              <FlipbookFlipContainer
                currentContent={currentContent}
                upcomingContent={upcomingContent}
                direction={state.flipDirection}
                onAnimationEnd={handleFlipComplete}
                mobile={breakpoint === 'mobile'}
              />
            </ParallaxProvider>

          </motion.div>
        </KeyManager>
      </Suspense>
    </FlipbookContext.Provider>
  );
});

// ─── FlipbookContext ──────────────────────────────────────────────────────────

export const FlipbookContext = React.createContext({
  showHighlight: false,
  fullscreen: false,
  subSlideOpen: false,
  activePageHasSubSlides: false,
  dimensions: { width: 0, height: 0 },
});

FlipbookContent.FlipbookContext = FlipbookContent;

// ─── Per-slide card background (`properties.{breakpoint}.background` on SLIDE) ─
// When a slide clears its background (`null`), the card must stay opaque — otherwise
// fold/slide animations show the next page through a transparent stack.

const FLIPBOOK_EMPTY_SLIDE_BACKGROUND = Object.freeze({
  type:  'empty',
  color: { type: 'solid', r: 255, g: 255, b: 255, a: 1 },
});

function isRenderableSlideBackground(bg) {
  return bg != null && typeof bg === 'object' && get(bg, 'type') != null;
}

/**
 * Resolves slide card background for flip animations. Flipbook slide backgrounds are
 * stored on `properties.desktop.background` for every viewport (mobile/tablet/desktop).
 * If the slide explicitly clears background (`null`), use an opaque white fill so folds
 * do not show the next layer through a transparent stack.
 * @param {string} [_breakpoint] retained for callers; not used for property lookup
 */
function resolveSlideBackgroundFromElement(slideEl, appearanceFallback, _breakpoint) {
  const wp = get(slideEl, 'props.widgetProps');
  const bg = wp ? get(wp, 'properties.desktop.background') : undefined;

  if (bg === null) {
    return FLIPBOOK_EMPTY_SLIDE_BACKGROUND;
  }
  if (isRenderableSlideBackground(bg)) {
    return bg;
  }
  if (isRenderableSlideBackground(appearanceFallback)) {
    return appearanceFallback;
  }
  return FLIPBOOK_EMPTY_SLIDE_BACKGROUND;
}

// ─── FlipbookPage ─────────────────────────────────────────────────────────────

export const FlipbookPage = React.memo(({ appearance, page, children, slideWidgets = [], loading = null }) => {

  const dispatch         = useDispatch();
  const flipbookPageRef  = useRef(null);
  const flipbookContext  = useContext_safe(FlipbookContext);
  const defaultSlideBg   = get(appearance, 'slideBackground');
  const breakpoint       = flipbookContext.breakpoint;
  const widgetContext    = React.useContext(WidgetsContext);

  // ── Sub-slide overlay (state lives in context so snapshots inherit it) ─────
  const subSlideOpen    = flipbookContext.subSlideOpen;
  const subSlideOpenRef = useRef(false);
  useEffect(() => { subSlideOpenRef.current = subSlideOpen; }, [subSlideOpen]);
  const setSubSlideOpen = flipbookContext.setSubSlideOpen || (() => {});
  const childrenArray   = React.Children.toArray(children);
  const coverSlide      = childrenArray[0] || null;
  const subSlides       = useMemo(() => childrenArray.slice(1), [childrenArray.length]); // eslint-disable-line
  const hasSubSlides    = subSlides.length > 0 || slideWidgets.length > 1;

  const coverBackground = useMemo(
    () => resolveSlideBackgroundFromElement(coverSlide, defaultSlideBg, breakpoint),
    [coverSlide, defaultSlideBg, breakpoint],
  );
  // ── Sub-slide flip state (self-contained within this component) ─────────────
  const currentSubSlideRef    = useRef(0); // 0-based index within subSlides array
  const isSubFlipAnimatingRef = useRef(false);
  const [subSlideFlip, setSubSlideFlip] = useState({
    currentIndex: 0,      // which sub-slide is currently shown
    direction:    null,    // 'next' | 'prev' | null
    targetIndex:  null,    // which sub-slide to flip to
  });
  // Mobile (breakpoint === 'mobile'): vertical stack index — desktop uses subSlideFlip + fold only
  const [mobileSubSlideIndex, setMobileSubSlideIndex] = useState(0);

  const idleSubSlideBackground = useMemo(
    () => resolveSlideBackgroundFromElement(
      subSlides[subSlideFlip.currentIndex] ?? null,
      defaultSlideBg,
      breakpoint,
    ),
    [subSlides, subSlideFlip.currentIndex, defaultSlideBg, breakpoint],
  );

  // ── refs that track mutable values without causing re-renders ────────────────
  // Global index into slideWidgets: 0 = cover, 1+ = sub-slides (same model as SpatialPage.currentSlideIndexRef)
  const currentSlideIndexRef = useRef(
    flipbookContext.activePage?.id === page?.id
      ? (flipbookContext.activePage?.slideIndex ?? 0)
      : 0
  );
  // startYRef tracks the Y position for VERTICAL slide scrolling (NOT horizontal page swipe)
  const startYRef        = useRef(0);
  const fullscreenRef    = useRef(flipbookContext.fullscreen);
  const activePageRef    = useRef(flipbookContext.activePage);
  const slideWidgetsRef  = useRef(slideWidgets);
  const prevActiveIdRef  = useRef(flipbookContext.activePage?.id === page?.id ? null : flipbookContext.activePage?.id);
  // Always-current flipbookContext for use inside event listeners (which have stale closures)
  const flipbookContextRef = useRef(flipbookContext);

  const slideDetails = useSelector(state => get(state, 'pages.spatial.hyperlinkSlide', {}));
  const pages        = useSelector(state => get(state, 'pages.spatial.items', []));
  const activePageId = activePageRef.current?.id || '';
  const carouselSlide= get(widgetContext, 'carouselSlide', null) || null;

  const url = useMemo(() => {
    return pages.find(obj => obj.id === slideDetails.page)?.canonical || '';
  }, [slideDetails.page, pages]);

  const editorWidgets = useSelector(
    state => {
      const fromEditors = get(state, `editors.${url}.widgets`);
      if (fromEditors) return get(state, `editors.${url}.widgets`, []);
      return get(state, `${widgetContext?.selector}.widgets`) || [];
    },
    isEqual,
  );

  const slideMemoIndex = useMemo(() => {
    try {
      const uid = (editorWidgets || []).find(obj => obj.uid === slideDetails?.uid)?.uid;
      if (uid) {
        const sorted = (editorWidgets || []).filter(d => d.type === SLIDE).sort((a, b) => a.order - b.order);
        return sorted.findIndex(obj => obj.uid === uid);
      }
      return -1;
    } catch { return -1; }
  }, [JSON.stringify(editorWidgets), slideDetails?.uid]);

  const widgetSlideIndex = useMemo(() => {
    try {
      return (slideWidgets || []).findIndex(obj => obj.uid === slideDetails?.uid);
    } catch { return -1; }
  }, [JSON.stringify(slideWidgets), slideDetails?.uid]);

  // sync refs
  useEffect(() => { slideWidgetsRef.current = slideWidgets; }, [slideWidgets]);
  // keep flipbookContextRef always pointing at the latest context value
  flipbookContextRef.current = flipbookContext;

  const syncActiveSlideIndex = useCallback((globalIndex) => {
    currentSlideIndexRef.current = globalIndex;
    const ctx = flipbookContextRef.current;
    if (!page?.id || ctx.activePage?.id !== page?.id) return;
    const prev = ctx.activePage?.slideIndex ?? 0;
    if (prev !== globalIndex) {
      ctx.navigateToPage(page.id, fullscreenRef.current, globalIndex);
    }
  }, [page?.id]);

  // ── Sub-slide flip content resolution ───────────────────────────────────────
  // Mirrors the page-level pattern: each sub-slide is wrapped as a card-sized
  // element centered in the full viewport, so the fold animation shows the card
  // edge rotating away with environment behind it — matching page-level flip.
  const subSlideOuterStyle = useMemo(() => ({
    position: 'relative', width: '100%', height: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }), []);
  const subSlideCardStyle = useMemo(() => ({
    width:  flipbookContext.dimensions.width,
    height: flipbookContext.dimensions.height,
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative',
  }), [flipbookContext.dimensions.width, flipbookContext.dimensions.height]);

  const wrapSubSlideForFlip = (index) => {
    const slide = subSlides[index];
    if (!slide) return null;
    const bg = resolveSlideBackgroundFromElement(slide, defaultSlideBg, breakpoint);
    return (
      <div key={`sub-flip-${index}`} style={subSlideOuterStyle}>
        <div style={subSlideCardStyle}>
          {bg && <BackgroundViewComponent background={bg} breakpoint={breakpoint} />}
          {slide}
        </div>
      </div>
    );
  };

  const subSlideCurrentContent = wrapSubSlideForFlip(subSlideFlip.currentIndex);
  const subSlideUpcomingContent = subSlideFlip.direction
    ? wrapSubSlideForFlip(subSlideFlip.targetIndex)
    : null;

  const handleSubSlideFlipComplete = useCallback(() => {
    isSubFlipAnimatingRef.current = false;
    setSubSlideFlip(s => ({
      currentIndex: s.targetIndex ?? s.currentIndex,
      direction:    null,
      targetIndex:  null,
    }));
  }, []);

  // ── Hyperlink / carousel navigation ─────────────────────────────────────────
  // scrollToSlide: if index 0 → stay on cover; if > 0 → open sub-slides + flip
  const scrollToSlide = useCallback((globalIndex) => {
    if (globalIndex < 0 || globalIndex >= slideWidgetsRef.current.length) return;

    const ctx  = flipbookContextRef.current;

    const useMobileStack = ctx.breakpoint === 'mobile';

    if (globalIndex === 0) {
      // Close sub-slides if open, stay on cover
      if (subSlideOpenRef.current) {
        setSubSlideOpen(false);
        subSlideOpenRef.current = false;
        currentSubSlideRef.current = 0;
        setSubSlideFlip({ currentIndex: 0, direction: null, targetIndex: null });
        if (useMobileStack) setMobileSubSlideIndex(0);
      }
      syncActiveSlideIndex(0);
      ctx.onChange?.('progress', { id: activePageRef.current.id, slideUid: slideWidgetsRef.current[0]?.uid, progress: 100 });
    } else {
      // Open sub-slides and navigate to the right sub-slide
      const subIdx = globalIndex - 1; // convert to 0-based sub-slide index
      if (!subSlideOpenRef.current) {
        setSubSlideOpen(true);
        subSlideOpenRef.current = true;
      }
      currentSubSlideRef.current = subIdx;
      setSubSlideFlip({ currentIndex: subIdx, direction: null, targetIndex: null });
      if (useMobileStack) setMobileSubSlideIndex(subIdx);
      syncActiveSlideIndex(globalIndex);
      ctx.onChange?.('progress', { id: activePageRef.current.id, slideUid: slideWidgetsRef.current[globalIndex]?.uid, progress: 100 });
    }
  }, [setSubSlideOpen, syncActiveSlideIndex]); // eslint-disable-line

  // hyperlink navigation
  useEffect(() => {
    if (!slideDetails.page || loading) return;
    if (slideDetails.page === activePageId && slideDetails.slide && slideMemoIndex > -1) {
      dispatch(updateHyperLinkSlide({ ...slideDetails, slide: '' }));
      return;
    }
    if (slideDetails.page !== activePageId) {
      flipbookContext.navigateToPage(slideDetails.page, fullscreenRef.current, 0);
    }
  }, [slideDetails, loading, fullscreenRef.current, activePageId]);

  useEffect(() => {
    try {
      if (widgetSlideIndex > -1 && slideDetails.page === activePageId && slideDetails.page === page?.id) {
        scrollToSlide(widgetSlideIndex);
        dispatch(updateHyperLinkSlide({ page: '', slide: '', uid: '' }));
      }
    } catch {}
  }, [slideDetails, activePageId, page?.id, slideWidgets]); // eslint-disable-line

  // carousel slide sync
  useEffect(() => {
    if (!carouselSlide?.carouselUid || !Array.isArray(editorWidgets) || !Array.isArray(slideWidgets)) return;
    const widget = editorWidgets.find(d => d.uid === carouselSlide.carouselUid);
    if (!widget) return;
    const slideIndex = slideWidgets.findIndex(d => d.uid === widget.parent);
    const globalIndex = currentSlideIndexRef.current;
    if (slideIndex === globalIndex) return;
    if (slideIndex > -1) scrollToSlide(slideIndex);
  }, [carouselSlide?.carouselUid, editorWidgets, slideWidgets]); // eslint-disable-line

  // Vertical swipe on cover (mobile only). Desktop/tablet: no wheel listener — trackpad scroll must not flip pages (menu / keyboard / controls still navigate).
  let isScrolling = false;
  const isMobileBreakpoint = breakpoint === 'mobile';

  useEffect(() => {
    const carousel = flipbookPageRef.current;
    if (!carousel || !isMobileBreakpoint) return;
    carousel.addEventListener('touchstart', handleTouchStart, { passive: true  });
    carousel.addEventListener('touchmove',  handleTouchMove,  { passive: false });
    carousel.addEventListener('touchend',   handleTouchEnd,   { passive: true  });
    return () => {
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchmove',  handleTouchMove);
      carousel.removeEventListener('touchend',   handleTouchEnd);
    };
  }, [isMobileBreakpoint]); // eslint-disable-line react-hooks/exhaustive-deps — handlers stable enough per breakpoint; full deps would re-bind every render

  const didMountRef      = useRef(false);
  const fullScreenClicked= useRef(false);

  useEffect(() => {
    fullscreenRef.current = flipbookContext.fullscreen;
    if (didMountRef.current) {
      fullScreenClicked.current = true;
    } else {
      didMountRef.current = true;
    }
  }, [flipbookContext.fullscreen]);

  useEffect(() => {
    const justBecameActive = flipbookContext.activePage?.id === page?.id &&
      prevActiveIdRef.current !== page?.id;
    prevActiveIdRef.current = flipbookContext.activePage?.id;

    activePageRef.current = flipbookContext.activePage;
    if (flipbookContext.activePage?.id === page?.id) {
      currentSlideIndexRef.current = flipbookContext.activePage?.slideIndex ?? 0;
    }
    if (fullScreenClicked.current) {
      currentSlideIndexRef.current = 0;
      currentSubSlideRef.current = 0;
      setMobileSubSlideIndex(0);
      if (flipbookContext.activePage?.id === page?.id) {
        syncActiveSlideIndex(0);
      }
    }
    const total = slideWidgetsRef.current.length;
    if (total === 1) {
      flipbookContext.onChange('progress', { id: activePageRef.current.id, slideUid: slideWidgetsRef.current[0]?.uid, progress: 100 });
    } else if (justBecameActive && total > 1) {
      flipbookContext.onChange?.('progress', { id: activePageRef.current.id, slideUid: slideWidgetsRef.current[0]?.uid, progress: 100 });
    }
    fullScreenClicked.current = false;
  }, [flipbookContext.activePage, page?.id, syncActiveSlideIndex]);

  // ══════════════════════════════════════════════════════════════════════════════
  // ── COVER TRACKER — page-to-page navigation ─────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  const handleOpenSubSlides = useCallback(() => {
    if (subSlideOpenRef.current || !hasSubSlides) return;
    currentSubSlideRef.current = 0;
    setSubSlideFlip({ currentIndex: 0, direction: null, targetIndex: null });
    setMobileSubSlideIndex(0);
    setSubSlideOpen(true);
    subSlideOpenRef.current = true;
    syncActiveSlideIndex(1);
    flipbookContextRef.current.onChange?.('progress', {
      id: activePageRef.current.id,
      slideUid: slideWidgetsRef.current[1]?.uid,
      progress: 100,
    });
  }, [hasSubSlides, setSubSlideOpen, syncActiveSlideIndex]);

  const handleCloseSubSlides = useCallback(() => {
    if (!subSlideOpenRef.current) return;
    setSubSlideOpen(false);
    subSlideOpenRef.current = false;
    currentSubSlideRef.current = 0;
    setSubSlideFlip({ currentIndex: 0, direction: null, targetIndex: null });
    setMobileSubSlideIndex(0);
    syncActiveSlideIndex(0);
  }, [setSubSlideOpen, syncActiveSlideIndex]);

  const handleNextPage = () => {
    const ctx      = flipbookContextRef.current;
    const allPages = ctx.pages;
    const idx      = allPages.findIndex(p => p.id === activePageRef.current?.id);
    const nextPage = allPages[idx + 1];
    if (nextPage) {
      const navOpts = subSlideOpenRef.current ? { closeDelayMs: 0 } : {};
      ctx.navigateToPage(nextPage.id, fullscreenRef.current, 0, true, navOpts);
    }
  };

  const handlePrevPage = () => {
    const ctx      = flipbookContextRef.current;
    const allPages = ctx.pages;
    const idx      = allPages.findIndex(p => p.id === activePageRef.current?.id);
    const prevPage = allPages[idx - 1];
    if (prevPage) {
      const navOpts = subSlideOpenRef.current ? { closeDelayMs: 0 } : {};
      ctx.navigateToPage(prevPage.id, fullscreenRef.current, 0, true, navOpts);
    }
  };

  // ── register page-level navigation into context (for Menu / keyboard) ───────
  // Only the FlipbookPage that matches context.activePage should register. During
  // mobile slide / desktop fold, multiple page trees mount; the last registration
  // could otherwise be an outgoing page that unmounts and leaves stale next/prev.
  useEffect(() => {
    if (page?.id !== flipbookContext.activePage?.id) return;
    const nav = {
      next: handleNextPage,
      prev: handlePrevPage,
      hasSubSlides,
      toggleSubSlides: () => {
        if (subSlideOpenRef.current) handleCloseSubSlides();
        else handleOpenSubSlides();
      },
    };
    flipbookContextRef.current.registerSlideNavigator?.(nav);
  }, [hasSubSlides, flipbookContext.activePage?.id, page?.id, flipbookContext.gatingActive]); // eslint-disable-line

  // ══════════════════════════════════════════════════════════════════════════════
  // ── SUB-SLIDE TRACKER — fold animation between sub-slides ───────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  const handleNextSubSlide = () => {
    if (isSubFlipAnimatingRef.current) return;
    const current  = currentSubSlideRef.current;
    const total    = subSlides.length;

    if (current < total - 1) {
      const target = current + 1;
      currentSubSlideRef.current = target;

      const globalIndex = target + 1;
      syncActiveSlideIndex(globalIndex);
      flipbookContextRef.current.onChange?.('progress', {
        id: activePageRef.current.id, slideUid: slideWidgetsRef.current[globalIndex]?.uid, progress: 100,
      });

      if (isMobileBreakpoint) {
        isSubFlipAnimatingRef.current = true;
        setMobileSubSlideIndex(target);
        setTimeout(() => { isSubFlipAnimatingRef.current = false; }, MOBILE_SUBSLIDE_STACK_MS);
        return;
      }

      isSubFlipAnimatingRef.current = true;
      setSubSlideFlip({ currentIndex: current, direction: 'next', targetIndex: target });
      return;
    }
    const ctx      = flipbookContextRef.current;
    const allPages = ctx.pages;
    const idx      = allPages.findIndex(p => p.id === activePageRef.current?.id);
    const nextPage = allPages[idx + 1];
    if (nextPage) {
      ctx.navigateToPage(nextPage.id, fullscreenRef.current, 0, true, { closeDelayMs: 0 });
    } else {
      handleCloseSubSlides();
    }
  };

  const handlePrevSubSlide = () => {
    if (isSubFlipAnimatingRef.current) return;
    const current = currentSubSlideRef.current;

    if (current > 0) {
      const target = current - 1;
      currentSubSlideRef.current = target;

      const globalIndex = target + 1;
      syncActiveSlideIndex(globalIndex);
      flipbookContextRef.current.onChange?.('progress', {
        id: activePageRef.current.id, slideUid: slideWidgetsRef.current[globalIndex]?.uid, progress: 100,
      });

      if (isMobileBreakpoint) {
        isSubFlipAnimatingRef.current = true;
        setMobileSubSlideIndex(target);
        setTimeout(() => { isSubFlipAnimatingRef.current = false; }, MOBILE_SUBSLIDE_STACK_MS);
        return;
      }

      isSubFlipAnimatingRef.current = true;
      setSubSlideFlip({ currentIndex: current, direction: 'prev', targetIndex: target });
    }
  };

  // ── scroll / touch navigation (cover card only) ─────────────────────────────
  const handleNavigate = (direction) => {
    if (subSlideOpenRef.current) return; // sub-slides open → button-driven only

    if (direction === 'down') {
      if (hasSubSlides) handleOpenSubSlides();
      else handleNextPage();
    } else {
      handlePrevPage();
    }
  };

  // ── touch handlers (VERTICAL reveal + flip) ───────────────────────────────────

  const handleTouchStart = (event) => {
    startYRef.current = event.touches[0].pageY;
  };

  const handleTouchMove = (event) => {
    event.preventDefault();
    if (isScrolling) return;

    const currentY = event.touches[0].pageY;
    const deltaY   = startYRef.current - currentY;

    if (Math.abs(deltaY) > 50) {
      isScrolling = true;
      handleNavigate(deltaY > 0 ? 'down' : 'up');
      setTimeout(() => { isScrolling = false; }, 1000);
    }
  };

  const handleTouchEnd = () => {
    startYRef.current = 0;
  };

  // ── Sub-slide swipe (mobile breakpoint only): vertical, spatial-style ───────
  const subSlideTouchStartXRef = useRef(0);
  const subSlideTouchStartYRef = useRef(0);
  const subSlideVertSwipeLockRef = useRef(false);

  const handleSubSlideTouchStart = useCallback((e) => {
    subSlideTouchStartXRef.current = e.touches[0].clientX;
    subSlideTouchStartYRef.current = e.touches[0].clientY;
  }, []);

  const handleSubSlideTouchEnd = (e) => {
    if (!isMobileBreakpoint) return;
    const deltaX = subSlideTouchStartXRef.current - e.changedTouches[0].clientX;
    const deltaY = subSlideTouchStartYRef.current - e.changedTouches[0].clientY;
    const threshold = 50;

    // Horizontal page change only when clearly horizontal *and* at first/last sub-slide.
    // Otherwise do not return — diagonal "swipe up" was being eaten here (|dX|>|dY|) with no nav.
    const ctx      = flipbookContextRef.current;
    const allPages = ctx.pages;
    const thisIdx  = allPages.findIndex(p => p.id === activePageRef.current?.id);
    const curSub   = currentSubSlideRef.current;
    const lastSub  = subSlides.length - 1;
    const strongHoriz = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= threshold;
    const canHorizNext = deltaX > 0 && curSub === lastSub && thisIdx < allPages.length - 1;
    const canHorizPrev = deltaX < 0 && curSub === 0 && thisIdx > 0;

    if (strongHoriz && (canHorizNext || canHorizPrev) && !subSlideVertSwipeLockRef.current) {
      subSlideVertSwipeLockRef.current = true;
      if (canHorizNext) {
        ctx.navigateToPage(allPages[thisIdx + 1].id, fullscreenRef.current, 0, true, { closeDelayMs: 0 });
      } else {
        ctx.navigateToPage(allPages[thisIdx - 1].id, fullscreenRef.current, 0, true, { closeDelayMs: 0 });
      }
      setTimeout(() => { subSlideVertSwipeLockRef.current = false; }, 1000);
      return;
    }

    if (Math.abs(deltaY) < threshold) return;

    // Swipe up on last inner — no-op (forward only between inners; book page via arrows / horizontal)
    if (deltaY > 0 && curSub >= lastSub) return;

    if (subSlideVertSwipeLockRef.current) return;
    subSlideVertSwipeLockRef.current = true;
    // Swipe up → next inner. Swipe down → close stack on first inner, else previous inner.
    if (deltaY > 0) {
      handleNextSubSlide();
    } else if (curSub <= 0) {
      handleCloseSubSlides();
    } else {
      handlePrevSubSlide();
    }
    setTimeout(() => { subSlideVertSwipeLockRef.current = false; }, 1000);
  };

  // ── Arrow disabled logic ─────────────────────────────────────────────────────
  const allPages    = flipbookContext.pages || [];
  const thisPageIdx = allPages.findIndex(p => p.id === page?.id);
  const isFirstPage = thisPageIdx === 0;
  const isLastPage  = thisPageIdx === allPages.length - 1;

  // Cover card: prev/next are page-level
  const isCoverPrevDisabled = isFirstPage;
  const isCoverNextDisabled = isLastPage;

  // Sub-slide card: prev/next are sub-slide-level
  const isSubPrevDisabled = isMobileBreakpoint ? mobileSubSlideIndex <= 0 : currentSubSlideRef.current <= 0;
  const isSubNextDisabled = isLastPage
    && (isMobileBreakpoint ? mobileSubSlideIndex >= subSlides.length - 1 : currentSubSlideRef.current >= subSlides.length - 1);

  // Reset overlay on page change (skip initial mount — flip slot mounts would close the overlay)
  const prevPageIdRef = useRef(flipbookContext.activePage?.id);
  useEffect(() => {
    if (prevPageIdRef.current === flipbookContext.activePage?.id) return; // same page, skip
    prevPageIdRef.current = flipbookContext.activePage?.id;
    setSubSlideOpen(false);
    subSlideOpenRef.current = false;
    currentSubSlideRef.current = 0;
    currentSlideIndexRef.current = 0;
    setSubSlideFlip({ currentIndex: 0, direction: null, targetIndex: null });
    setMobileSubSlideIndex(0);
  }, [flipbookContext.activePage?.id]);

  const subSlidePaneH = flipbookContext.dimensions.height || 0;

  return (
    <motion.div
      initial={{ opacity: 1 }}
      id={page && page.id}
      className={styles.flipbookPage}
    >
      {/* ── Wrapper keeps all layers aligned to card dimensions ── */}
      <div
        className={styles.flipbookPageWrapper}
        style={{
          width:  `${flipbookContext.dimensions.width}px`,
          height: `${flipbookContext.dimensions.height}px`,
        }}
      >
        {/* ══ COVER CARD (slide 1 only) ══ */}
        <div
          id={`cs-flipbook-page-${page?.id}`}
          className={cx(styles.flipbookPageInner, 'flipbookContainer',
            { [styles.flipbookPageInnerHidden]: subSlideOpen && isMobileBreakpoint },
            { [styles.highlighted]: flipbookContext.showHighlight },
            { [styles.fullscreen]:  flipbookContext.fullscreen }
          )}
        >
          {loading && <LoadingMessage />}
          {coverBackground && <BackgroundViewComponent background={coverBackground} breakpoint={breakpoint} />}

          <div id='cs-flipbook-slide' ref={flipbookPageRef} className={cx(styles.flipbookPageContainer)}>
            {coverSlide}
          </div>

          {/* Cover card nav buttons */}
          {!loading && !flipbookContext.gatingActive && (
            <div className={styles.cardNavControls}>
              <button
                className={cx(styles.pageNavArrow, styles.pageNavArrowPrev, {
                  [styles.pageNavArrowDisabled]: isCoverPrevDisabled,
                })}
                onClick={handlePrevPage}
                disabled={isCoverPrevDisabled}
                aria-label="Previous page"
              >
                <i className="cleverstory-drop-down-left" />
              </button>

              <button
                className={cx(styles.pageNavArrow, styles.pageNavArrowNext, {
                  [styles.pageNavArrowDisabled]: isCoverNextDisabled,
                })}
                onClick={handleNextPage}
                disabled={isCoverNextDisabled}
                aria-label="Next page"
              >
                <i className="cleverstory-drop-down-right" />
              </button>

              {(hasSubSlides && breakpoint !== 'mobile' ) && (
                <button
                  className={cx(styles.dropdownArrow)}
                  onClick={handleOpenSubSlides}
                  aria-label="Open sub-slides"
                >
                  <i className="cleverstory-drop-down" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ══ SUB-SLIDE CARD (at page level — slides in as a separate card) ══ */}
      {hasSubSlides && (
        <div
          className={cx(styles.subSlideCard, { [styles.subSlideCardVisible]: subSlideOpen })}
          style={{
            width:  `${flipbookContext.dimensions.width}px`,
            height: `${flipbookContext.dimensions.height}px`,
          }}
        >
          {isMobileBreakpoint ? (
            <div
              className={styles.subSlideVerticalViewport}
              onTouchStart={handleSubSlideTouchStart}
              onTouchEnd={handleSubSlideTouchEnd}
            >
              <div
                className={styles.subSlideVerticalTrack}
                style={{
                  height:     subSlidePaneH * subSlides.length,
                  transform:  `translateY(-${mobileSubSlideIndex * subSlidePaneH}px)`,
                  transition: `transform ${MOBILE_SUBSLIDE_STACK_MS}ms ease`,
                }}
              >
                {subSlides.map((slide, i) => {
                  const bg = resolveSlideBackgroundFromElement(slide, defaultSlideBg);
                  return (
                    <div
                      key={`mob-sub-${page?.id}-${i}`}
                      className={styles.subSlideVerticalPane}
                      style={{ height: subSlidePaneH || '100%' }}
                    >
                      {bg && <BackgroundViewComponent background={bg} breakpoint={breakpoint} />}
                      {slide}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', visibility: subSlideFlip.direction ? 'hidden' : 'visible' }}>
              {idleSubSlideBackground && (
                <BackgroundViewComponent background={idleSubSlideBackground} breakpoint={breakpoint} />
              )}
              {subSlides[subSlideFlip.currentIndex]}
            </div>
          )}

          {/* Sub-slide card nav buttons */}
          {!flipbookContext.gatingActive && <div className={styles.cardNavControls}>
            <button
              className={cx(styles.pageNavArrow, styles.pageNavArrowPrev, {
                [styles.pageNavArrowDisabled]: isSubPrevDisabled,
              })}
              onClick={handlePrevSubSlide}
              disabled={isSubPrevDisabled}
              aria-label="Previous sub-slide"
            >
              <i className="cleverstory-drop-down-left" />
            </button>

            <button
              className={cx(styles.pageNavArrow, styles.pageNavArrowNext, {
                [styles.pageNavArrowDisabled]: isSubNextDisabled,
              })}
              onClick={handleNextSubSlide}
              disabled={isSubNextDisabled}
              aria-label="Next sub-slide"
            >
              <i className="cleverstory-drop-down-right" />
            </button>

          {  breakpoint !== 'mobile' &&
            <button
              className={cx(styles.dropdownArrow, styles.dropdownArrowOpen)}
              onClick={handleCloseSubSlides}
              aria-label="Close sub-slides"
            >
              <i className="cleverstory-drop-down" />
            </button>
          }
          </div>}
        </div>
      )}

      {/* ══ SUB-SLIDE FLIP OVERLAY (at page level, only during animation) ══
          Rendered as sibling of .flipbookPageWrapper so .flipViewport fills
          the full .flipbookPage (100vw × 100%) — matching page-level flip.
          Each sub-slide is wrapped as a centered card, giving the fold
          breathing room and depth identical to page-to-page flip. */}
      {!isMobileBreakpoint && subSlideFlip.direction && !flipbookContext.flipDirection && (
        <FlipbookFlipContainer
          currentContent={subSlideCurrentContent}
          upcomingContent={subSlideUpcomingContent}
          direction={subSlideFlip.direction}
          onAnimationEnd={handleSubSlideFlipComplete}
          mobile={isMobileBreakpoint}
        />
      )}
    </motion.div>
  );
});

// tiny helper so we don't duplicate React.useContext calls
function useContext_safe(Context) {
  return React.useContext(Context);
}

FlipbookContent.FlipbookPage = FlipbookPage;

// ─── Loading indicator ────────────────────────────────────────────────────────

const LoadingMessage = () => (
  <div className={styles.loadingMessage}>
    <span className={styles.loadingDot} />
    <span className={styles.loadingDot} />
    <span className={styles.loadingDot} />
  </div>
);

// ─── Arrow buttons ────────────────────────────────────────────────────────────

const LeftArrow = React.memo(({ pages, activePage, menuDesigns, iconStyle, onClick }) => {
  const currentIndex = pages.findIndex(obj => obj.id === activePage.id);
  const isDisabled   = currentIndex === 0;
  const regularStyle = useMemo(() => getStyles(menuDesigns, 'regular'), [menuDesigns]);
  const hoverStyle   = useMemo(() => getStyles(menuDesigns, 'hover'),   [menuDesigns]);
  const [isHovered, setIsHovered] = useState(false);
  const buttonStyle  = useMemo(() => {
    if (isDisabled) return regularStyle;
    return isHovered ? hoverStyle : regularStyle;
  }, [isHovered, isDisabled, regularStyle, hoverStyle]);

  return (
    <Button
      variant="info" id="leftArrow" disabled={isDisabled} style={buttonStyle}
      onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      onClick={onClick} className={styles.arrowButton}
    >
      <i style={{ color: iconStyle.color }} className={cx(styles.icon, 'cleverstory-drop-down-left')} />
    </Button>
  );
});

const RightArrow = React.memo(({ pages, activePage, menuDesigns, iconStyle, onClick }) => {
  const currentIndex = pages.findIndex(obj => obj.id === activePage.id);
  const isDisabled   = currentIndex === pages.length - 1;
  const regularStyle = useMemo(() => getStyles(menuDesigns, 'regular'), [menuDesigns]);
  const hoverStyle   = useMemo(() => getStyles(menuDesigns, 'hover'),   [menuDesigns]);
  const [isHovered, setIsHovered] = useState(false);
  const buttonStyle  = useMemo(() => {
    if (isDisabled) return regularStyle;
    return isHovered ? hoverStyle : regularStyle;
  }, [isHovered, isDisabled, regularStyle, hoverStyle]);

  return (
    <Button
      variant="info" id="rightArrow" disabled={isDisabled} style={buttonStyle}
      onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      onClick={onClick} className={styles.arrowButton}
    >
      <i style={{ color: iconStyle.color }} className={cx(styles.icon, 'cleverstory-drop-down-right')} />
    </Button>
  );
});

// ─── Menu ─────────────────────────────────────────────────────────────────────

const Menu = React.memo(({
  menuPosition, onToggleHighlight, appearance,
  onToggleFullscreen, fullscreen, activePage,
  navigateToPage, breakpoint, pages,
}) => {
  const ref       = useRef(null);
  const scrollRef = useRef(null);
  const flipbookContext = React.useContext(FlipbookContext);
  const desktop   = useDesktop();
  const menuDesigns = get(appearance, 'menuDesign');
  const gatingActive = !!flipbookContext.gatingActive;
  
  const [state, setState] = useState({ show: false });
  const prevGatingActiveRef = useRef(undefined);

  const menuStyles = useMemo(() => ({
    containerStyle: getWrapperStyles(get(appearance, 'menuDesign')),
    buttonStyle:    getStyles(get(appearance, 'menuDesign'), 'regular'),
    iconStyle:      getIconStyles(get(appearance, 'menuDesign')),
  }), [appearance]);

  useEffect(() => {
    if (menuPosition.y === 0) return;
    setTimeout(() => setState(s => ({ ...s, show: true })), 1000);
  }, [menuPosition.y]);

  useEffect(() => {
    if (gatingActive) {
      setState(s => ({ ...s, show: false }));
    } else if (prevGatingActiveRef.current === true) {
      // Restore toolbar after gating closes — even if menuPosition.y hasn't changed.
      setState(s => ({ ...s, show: true }));
    }
    prevGatingActiveRef.current = gatingActive;
  }, [gatingActive]);

  useEffect(() => {
    const pageIndex = pages.findIndex(obj => obj.id === activePage.id);
    if (scrollRef.current && !scrollRef.current.isItemVisible(pageIndex)) {
      scrollRef.current.scrollToItem(scrollRef.current.getItemByIndex(pageIndex));
    }
  }, [activePage]);

  const handleLeftArrowClick = useCallback(() => {
    // Delegate to the smart action registered by the active FlipbookPage.
    // It decides: scroll to prev slide within the page, OR flip to prev page.
    flipbookContext.navigatePrevAction?.();

    // Keep the horizontal scroll-menu in sync with whichever page ends up active.
    // The existing `useEffect([activePage])` below handles this automatically once
    // activePage updates, so no manual scrollToItem needed here.
  }, [flipbookContext.navigatePrevAction]);

  const handleRightArrowClick = useCallback(() => {
    // Delegate to the smart action registered by the active FlipbookPage.
    // It decides: scroll to next slide within the page, OR flip to next page.
    flipbookContext.navigateNextAction?.();
  }, [flipbookContext.navigateNextAction]);

  const handleDropdownToggle = useCallback(() => {
    flipbookContext.toggleSubSlidesAction?.();
  }, [flipbookContext.toggleSubSlidesAction]);

  const style = desktop
    ? { transform: `translateX(0px)` }
    : {
        position: 'fixed',
        left:     `${menuPosition.x}px`,
        top:      `${menuPosition.y}px`,
        transform: 'none',
      };

  const MenuItem = React.memo(({ title, activePage, itemId, page }) => {
    const isActive     = activePage.id === page.id;
    const regularStyle = useMemo(() => getStyles(menuDesigns, 'regular'), [menuDesigns]);
    const hoverStyle   = useMemo(() => getStyles(menuDesigns, 'hover'),   [menuDesigns]);
    const ref          = useRef(null);
    const applyHover   = (s) => { if (ref.current) Object.assign(ref.current.style, s); };

    return (
      <div
        ref={ref} role="menuitem" id={page.id} tabIndex={0} key={itemId}
        style={isActive ? hoverStyle : regularStyle}
        onMouseEnter={() => applyHover(hoverStyle)}
        onMouseLeave={() => applyHover(isActive ? hoverStyle : regularStyle)}
        onClick={() => navigateToPage(page.id, false, 0, true)}
        className={cx(styles.menuItemButton)}
      >
        <h3 className={styles.menuItemTitle}>
          <TruncatedTextWithTooltip className={styles.menuItemText} content={title} maxWidth={150} />
        </h3>
      </div>
    );
  });

  return (
    <div
      id="cs-flipbook-menu"
      ref={ref}
      style={style}
      className={cx(styles.toolbar, {
        [styles.toolbarTop]: desktop,
        [styles.toolbarMobileInCard]: !desktop,
        [styles.show]: state.show,
        [styles.fullscreen]: fullscreen,
        [styles.toolbarGatingHidden]: gatingActive,
      })}
    >
      {(desktop
        ? [
            <div key="flipbook-menu-desktop-scroll" style={menuStyles.containerStyle} className={styles.mainMenu}>
              <div style={{ maxWidth: '700px', overflow: 'hidden' }}>
                <ScrollMenu
                  apiRef={scrollRef}
                  scrollContainerClassName={styles.scrollingMenu}
                  wrapperClassName={styles.scrollWrapper}
                  LeftArrow={
                    <LeftArrow pages={pages} activePage={activePage} menuDesigns={menuDesigns}
                      iconStyle={menuStyles.iconStyle} onClick={handleLeftArrowClick} />
                  }
                  RightArrow={
                    <RightArrow pages={pages} activePage={activePage} menuDesigns={menuDesigns}
                      iconStyle={menuStyles.iconStyle} onClick={handleRightArrowClick} />
                  }
                >
                  {pages && pages.filter(page => !page.hidden).map((page, index) => (
                    <MenuItem key={index} itemId={index} activePage={activePage} page={page} title={page.name} />
                  ))}
                </ScrollMenu>
              </div>
            </div>,
            <div key="flipbook-menu-desktop-fs" style={menuStyles.containerStyle} className={styles.mainMenu}>
              <div className={styles.menuIcon} onClick={onToggleFullscreen}>
                <i style={{ color: menuStyles.iconStyle.color }} className={cx(styles.icon, 'cleverstory-expand-editor')} />
              </div>
              <style>{`.menuIcon i::before { color: ${getColorsConfig(menuStyles.iconStyle.color)}; }`}</style>
            </div>,
          ]
        : [
            <div key="flipbook-menu-mobile-arrows" style={menuStyles.containerStyle} className={styles.mainMenu}>
              <LeftArrow pages={pages} activePage={activePage} menuDesigns={menuDesigns} iconStyle={menuStyles.iconStyle} onClick={handleLeftArrowClick} />
              <RightArrow pages={pages} activePage={activePage} menuDesigns={menuDesigns} iconStyle={menuStyles.iconStyle} onClick={handleRightArrowClick} />
            </div>,
            ...(flipbookContext.activePageHasSubSlides ? [
              <div key="flipbook-menu-mobile-toggle" style={menuStyles.containerStyle} className={styles.mainMenu}>
                <button
                  className={cx(styles.menuDropdownToggle, {
                    [styles.menuDropdownToggleOpen]: flipbookContext.subSlideOpen,
                  })}
                  onClick={handleDropdownToggle}
                  aria-label="Toggle sub-slides"
                >
                  <i style={{ color: menuStyles.iconStyle.color }} className="cleverstory-drop-down" />
                </button>
              </div>,
            ] : []),
          ])}
    </div>
  );
});

// ─── Style helpers (unchanged) ────────────────────────────────────────────────

function getWrapperStyles(properties) {
  if (!properties) return {};
  const backgroundRgb = get(properties, 'background', null);
  const blur          = get(properties, 'background.blur', 20);
  return {
    background:              backgroundRgb ? getColorsConfig(backgroundRgb) : 'inherit',
    '-webkit-backdrop-filter': `blur(${blur}px)`,
    'backdrop-filter':         `blur(${blur}px)`,
  };
}

function getIconStyles(properties) {
  if (!properties) return {};
  const colorRgb = get(properties, 'regular.font.color', null);
  return getColorStyles(colorRgb) || {};
}

function getStyles(properties, active) {
  if (!properties) return {};
  const defaultStyles  = get(properties, 'regular', {});
  const colorRgb       = get(defaultStyles, 'font.color', null);
  const color          = getColorStyles(active === 'regular' ? { ...colorRgb, a: 0.60 } : colorRgb);
  const backgroundRgb  = get(properties, `${active}.background`, null);
  const fontFamily     = get(defaultStyles, 'font.fontFamily');
  return {
    ...(color || {}),
    background: backgroundRgb && active === 'hover' ? getColorsConfig(backgroundRgb) : 'inherit',
    ...(fontFamily ? { fontFamily: injectAktivFallback(fontFamily) } : {}),
  };
}

export function getColorStyles(value) {
  if (!value) return null;
  const color = `rgba(${value.r}, ${value.g}, ${value.b}, ${value.a})`;
  return { color };
}

// ─── useViewportSize ──────────────────────────────────────────────────────────

function useViewportSize() {
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0, viewportHeight: '100vh' });
  const [mounted, setMounted]           = useState(false);

  useEffect(() => {
    setMounted(true);
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      const vh = (window.visualViewport ? window.visualViewport.height : window.innerHeight) * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.body.style.height = `${vh * 100}px`;
      setViewportSize({
        width:          window.visualViewport?.width  || window.innerWidth,
        height:         window.visualViewport?.height || window.innerHeight,
        viewportHeight: `${vh * 100}px`,
      });
    };
    handleResize();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  if (!mounted) return { width: 1200, height: 800, viewportHeight: '100vh' };
  return viewportSize;
}
